/**
 * Smart-navigation second opinion.
 *
 * Given the candidate routes plus this driver's own history for the same
 * destination, a cheap model (Haiku, server-side - see api/index.php) says
 * which one it would take and why. The value is not "which is fastest" (the
 * routing engine already answers that) but the trade the engine won't make:
 * a route six minutes longer with eighteen fewer traffic lights, or a route
 * whose estimate this driver has beaten every time for the last month.
 *
 * Everything about it is best-effort. No advice, a network failure, or a
 * server with no API key configured must never block starting a drive - the
 * card simply doesn't appear.
 */

import type { LngLat, Place, Route, Trip } from '../core/types';
import type { RouteStats } from '../nav/routeStats';
import { summariseRoute } from '../nav/directions';
import { activeVehicle, type Settings } from '../core/settings';
import { distance } from '../core/geo';
import { routeLetter } from '../core/format';
import { paceDuration } from '../nav/paceModel';
import type { ElevationProfile } from '../nav/elevation';

const API = `${import.meta.env.BASE_URL}api`;

export interface RouteAdvice {
  /** Index into the routes array that was sent. */
  pick: number;
  confidence: 'low' | 'medium' | 'high';
  headline: string;
  reason: string;
  tips: string[];
}

export interface AdviseInput {
  destination: Place;
  routes: Route[];
  stats: RouteStats[];
  trips: Trip[];
  settings: Settings;
  elevation?: ElevationProfile | null;
}

/** Trips that ended near this destination, most recent first. */
export function relevantTrips(trips: Trip[], destination: LngLat, limit = 12): Trip[] {
  return trips
    .filter((t) => t.destination && distance(t.destination, destination) < 400)
    .sort((a, b) => b.startedAt - a.startedAt)
    .slice(0, limit);
}

function round(n: number, dp = 1): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/**
 * Stable identity for a request, so re-rendering the preview or toggling a
 * chip doesn't re-bill us for the same question.
 */
export function adviceKey(input: AdviseInput): string {
  const r = input.routes
    .map((x) => `${Math.round(x.distance)}:${Math.round(paceDuration(x) / 30)}`)
    .join('|');
  const d = input.destination.center.map((c) => c.toFixed(3)).join(',');
  return `${d}#${r}#${new Date().getHours()}`;
}

export async function fetchAdvice(
  input: AdviseInput,
  signal?: AbortSignal,
): Promise<RouteAdvice | null> {
  const { destination, routes, stats, trips, settings } = input;
  if (routes.length < 2) return null; // nothing to choose between

  const now = new Date();
  const vehicle = activeVehicle(settings);

  const body = {
    lang: settings.language,
    destination: {
      name: destination.name,
      lat: round(destination.center[1], 4),
      lng: round(destination.center[0], 4),
    },
    departure: {
      hour: now.getHours(),
      weekday: now.toLocaleDateString('en-US', { weekday: 'long' }),
    },
    vehicle: { name: vehicle.name, kind: vehicle.kind },
    routes: routes.map((r, i) => ({
      index: i,
      // The letter the driver sees on screen. Anything else and the advice
      // names a route they cannot find.
      name: routeLetter(i),
      label: r._tag ?? `route ${routeLetter(i)}`,
      via: summariseRoute(r),
      distanceKm: round(r.distance / 1000),
      // Live traffic capped at the driver's own top speeds, which is what they
      // see on the sheet. `minutesAtLights` is a separate descriptive figure -
      // it is NOT part of this total. See nav/paceModel.ts.
      durationMin: Math.round(paceDuration(r) / 60),
      routerDurationMin: Math.round(r.duration / 60),
      minutesAtLights: Math.round((r._paceSignalSeconds ?? 0) / 60),
      typicalMin: r.duration_typical ? Math.round(r.duration_typical / 60) : null,
      trafficSignals: stats[i]?.trafficSignals ?? null,
      turns: stats[i]?.turns ?? null,
      tollBooths: stats[i]?.tollBooths ?? null,
      smoothness: stats[i]?.smoothness ?? null,
    })),
    // Gradient is often the deciding factor for a loaded truck, and it is not
    // something the routing engine weighs at all.
    terrain: input.elevation
      ? {
          totalClimbM: input.elevation.totalAscent,
          steepestClimbPct: input.elevation.steepestClimb,
          steepestDescentPct: input.elevation.steepestDescent,
          beyondVehicle: input.elevation.blocking,
          hardSections: input.elevation.notable.slice(0, 3).map((s) => ({
            gradePct: s.grade,
            lengthKm: round((s.to - s.from) / 1000),
            uphill: s.climb,
            severity: s.severity,
          })),
        }
      : null,
    history: relevantTrips(trips, destination.center).map((t) => ({
      daysAgo: Math.round((Date.now() - t.startedAt) / 86_400_000),
      hour: new Date(t.startedAt).getHours(),
      weekday: new Date(t.startedAt).toLocaleDateString('en-US', { weekday: 'long' }),
      durationMin: Math.round(t.duration / 60),
      distanceKm: round(t.distance / 1000),
      vehicle: t.vehicleId,
    })),
  };

  const res = await fetch(`${API}/ai/advise`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: signal ?? AbortSignal.timeout(30_000),
  });

  if (!res.ok) {
    // 503 = no key configured, 429 = daily cap. Both are non-events for the UI.
    return null;
  }
  const json = (await res.json()) as { advice?: RouteAdvice };
  const advice = json.advice;
  if (!advice || typeof advice.pick !== 'number') return null;
  if (advice.pick < 0 || advice.pick >= routes.length) return null;
  return { ...advice, tips: Array.isArray(advice.tips) ? advice.tips.slice(0, 2) : [] };
}
